import React, { useEffect, useState } from 'react';
import { find, flatten, partition } from 'lodash';
import moment from 'moment-timezone';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';

import { ModalProvider, useModalContext } from '@magoosh/context/modal_context';
import CardLayout from '@magoosh/layouts/card';
import { DefaultFilledButton, SecondaryButton, TabButton } from '@magoosh/lib/button';
import { Select } from '@magoosh/lib/forms';
import {
  FontAwesomeIcon,
  LightBulbPlantIcon,
  MagnifyingGlassIcon,
  WateringSeedlingIcon
} from '@magoosh/lib/icons';
import AnswersTable from '@magoosh/organisms/answers_table';
import ScoreCard from '@magoosh/organisms/score_card';
import ScoreReportStatusCard from '@magoosh/organisms/score_report_status_card/multiple';
import { paths } from 'config/path_helpers';
import { MockTest, MockTestScore } from 'types/mock_test';
import { toSentence } from 'utilities/formatting';

import GradientPill from '@test_prep/app/ai_tutor/components/gradient_pill';
import { GraduateCapIcon } from '@test_prep/app/ai_tutor/components/icons';
import { useAppContext } from '@test_prep/app/context';
import ContextualLink from '@test_prep/app/components/contextual_link';

import PerformanceBreakdown from './performance_breakdown';
import { ExamSectionDetail } from './types';
import ViewingSharedResultsBanner from './viewing_shared_results_banner';
import UpsellCard from '../components/upsell_card';

import colors from '@magoosh/lib/styles/colors.module.scss';

interface Props {
  examSectionDetails: ExamSectionDetail[];
  mockTest: MockTest;
  refreshData: () => void;
  ui?: {
    initialExamSection?: string;
    saleMessage: string;
    showTutorNextStep?: boolean;
  }
}

interface ScoreCardRow {
  backgroundColor?: string;
  scores: MockTestScore[];
  wide: boolean;
}

// A slightly darker green than the default score card background, used to set the ACT's
// Composite and STEM scores apart from its section scores
const ACT_PRIMARY_SCORE_BACKGROUND = '#A5E1C8';

const MockTestResults: React.FC<Props> = (props) => {
  const { branding, features } = useAppContext().data;
  const history = useHistory();
  const { id, shareableId } = useParams();
  const { setModal } = useModalContext();

  const showUpsell = branding.accessLevel !== 'premium';
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const defaultExamSection = () => {
    const initialExamSection = props.examSectionDetails.find((examSection) => (
      examSection.slug === props.ui.initialExamSection
    ));

    return initialExamSection || props.examSectionDetails[0];
  };

  const [currentExamSection, setCurrentExamSection] = useState(defaultExamSection());

  // If mock test data has refreshed, pull the updates into the currentExamSection
  useEffect(() => {
    if (currentExamSection) {
      setCurrentExamSection(
        props.examSectionDetails.find((examSection) => (
          examSection.slug === currentExamSection.slug
        ))
      );
    } else {
      setCurrentExamSection(defaultExamSection());
    }
  }, [props.mockTest]);

  const answersWithScoreReports = () => {
    const answers = flatten(props.examSectionDetails.map((examSection) => (
      flatten((examSection.answersBySection || []).map((section) => section.answers))
    )));

    return answers.filter((answer) => !!answer.scoreReport)
  };

  const handleCurrentExamSectionChanged = (slug) => {
    const newExamSection = find(props.examSectionDetails, (examSection) => (
      examSection.slug === slug
    ));

    if (!shareableId) {
      window.history.replaceState(null, null, `${paths.practiceTest(props.mockTest.id)}?exam_section=${newExamSection.slug}`);
    }

    setCurrentExamSection(newExamSection);
  };

  // Groups the scores into the rows of score cards to display
  const scoreCardRows = (): ScoreCardRow[] => {
    const totalScore = props.mockTest.score ? [props.mockTest.score] : [];

    // The ACT gives the Composite and STEM scores a row of their own, in wider and darker
    // cards, with the remaining section scores in a second row
    if (branding.examName === 'ACT') {
      const [stemScores, sectionScores] = partition(
        props.mockTest.sectionScores,
        (sectionScore) => sectionScore.name === 'STEM'
      );

      return [
        {
          backgroundColor: ACT_PRIMARY_SCORE_BACKGROUND,
          scores: [...totalScore, ...stemScores],
          wide: true
        },
        { scores: sectionScores, wide: false }
      ].filter((row) => row.scores.length > 0);
    }

    return [{ scores: [...totalScore, ...props.mockTest.sectionScores], wide: false }];
  };

  const shareResultModal = () => {
    const shareTestUrl = `${window.location.origin}${paths.practiceTestsShare(props.mockTest.shareableId)}`;

    const copyShareLink = async (className: string) => {
      const element = document.querySelector(`.${className}`) as HTMLElement | null;
      const textToCopy = element.textContent;
      await navigator.clipboard.writeText(textToCopy);
      element.innerText = 'Copied!';
      setTimeout(() => {
        element.innerText = textToCopy;
      }, 1000);
    };

    setModal({
      header: 'Share Your Results',
      body: (
        <div>
          <p>Sharing results will let a tutor view your test scores, performance breakdown, and answers to specific questions.</p>
          <p>
            <h5><b><u>Share link</u></b></h5>
            <div className="d-flex">
              <pre className="share-link" style={{
                whiteSpace: 'break-spaces',
                borderRadius: '8px 0 0 8px',
                borderRight: 'none',
                width: '100%'
              }}>
                {shareTestUrl}
              </pre>
              <button className="btn btn-default" type="button" onClick={() => copyShareLink('share-link')} style={{
                marginBottom: '12px',
                borderRadius: '0 8px 8px 0',
                borderWidth: '1px'
              }}>
                <FontAwesomeIcon icon="copy" />
              </button>
            </div>
          </p>
          <SecondaryButton block onClick={() => setModal(null)}>Close</SecondaryButton>
        </div>
      )
    })
  };

  return (
    <>
      <Helmet>
        <title>{branding.examName} Test Results - Magoosh {branding.examName}</title>
      </Helmet>

      {
        answersWithScoreReports().length > 0 && !shareableId && (
          <div className="u-margin-B-s">
            <ScoreReportStatusCard
              answers={answersWithScoreReports()}
              onFinished={props.refreshData}
            />
          </div>
        )
      }

      <div className="d-flex flex-column gap-m">
        <CardLayout.Card className="d-flex flex-column flex-lg-row gap-m">
          <div>
            <h1 className="h2 u-margin-A-n u-padding-B-xxs">
              <strong>Practice Test Results</strong>
            </h1>

            <div><small>Test <strong>{props.mockTest.name}</strong></small></div>
            <div>
              <small>
                Finished {
                  moment(props.mockTest.finishedAt * 1000).tz(timeZone).isSame(moment().tz(timeZone), 'day')
                    ? 'Today'
                    : moment(props.mockTest.finishedAt * 1000).tz(timeZone).fromNow()
                }
              </small>
            </div>

            {
              !shareableId && (
                <div>
                  <small>
                    <a onClick={shareResultModal}>
                      <FontAwesomeIcon icon="share"/>
                      {' '}Share your results
                    </a>
                  </small>
                </div>
              )
            }
          </div>

          <div className="d-flex flex-1 flex-column align-items-lg-end gap-s">
            {
              scoreCardRows().map((row, idx) => (
                <div
                  key={idx}
                  className="d-flex flex-1 flex-column flex-lg-row flex-lg-wrap gap-s justify-content-lg-end"
                >
                  {
                    row.scores.map((score) => (
                      <ScoreCard
                        backgroundColor={row.backgroundColor}
                        key={score.name}
                        name={score.name}
                        score={score.value}
                        scoreReason={score.explanation}
                        wide={row.wide}
                      />
                    ))
                  }
                </div>
              ))
            }

            {
              props.mockTest.score?.equivalents && (
                <div className="text-gray-light" style={{ fontSize: 12 }}>
                  Your score is similar to {toSentence(props.mockTest.score.equivalents.map((equivalent) => `${equivalent.name} ${equivalent.value}`))}.
                </div>
              )
            }
          </div>
        </CardLayout.Card>

        <div className="d-flex flex-column-reverse flex-md-column gap-m">
          {
            !shareableId && (
              <div className="d-flex flex-column flex-md-row gap-m">
                {(() => {
                  if (showUpsell && props.ui?.showTutorNextStep) {
                    return (
                      <>
                        <div style={{ flex: 3 }}>
                          <AITutorNextStep branding={branding} />
                        </div>

                        <div style={{ flex: 5 }}>
                          <UpsellCard examName={branding.examName} saleMessage={props.ui?.saleMessage} />
                        </div>
                      </>
                    );
                  } else if (showUpsell) {
                    return (
                      <>
                        <div style={{ flex: 3 }}>
                          <ReviewPerformanceNextStep mockTest={props.mockTest} />
                        </div>

                        <div style={{ flex: 5 }}>
                          <UpsellCard examName={branding.examName} saleMessage={props.ui?.saleMessage} />
                        </div>
                      </>
                    );
                  } else if (props.ui?.showTutorNextStep) {
                    return (
                      <>
                        <div className="flex-1">
                          <ReviewPerformanceNextStep mockTest={props.mockTest} />
                        </div>

                        <div className="flex-1">
                          <AITutorNextStep branding={branding} />
                        </div>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <div className="flex-1">
                          <ReviewPerformanceNextStep mockTest={props.mockTest} />
                        </div>

                        <div className="flex-1">
                          <DecideOnNextTestNextStep />
                        </div>
                      </>
                    );
                  }
                })()}
              </div>
            )
          }

          <div className="d-flex flex-column gap-m">
            <div>
              <h3 className="u-margin-A-n u-margin-T-s">
                <strong>Section Results</strong>
              </h3>

              {
                props.examSectionDetails.length > 1 && (
                  <>
                    <div className="hidden-xs">
                      {
                        props.examSectionDetails.map((examSection) => (
                          <TabButton
                            disabled={examSection === currentExamSection}
                            key={examSection.slug}
                            onClick={() => handleCurrentExamSectionChanged(examSection.slug)}
                          >
                            {examSection.name}
                          </TabButton>
                        ))
                      }
                    </div>

                    <div className="visible-xs">
                      <Select
                        groupClassName="u-margin-B-n u-margin-T-s"
                        onChange={(e) => handleCurrentExamSectionChanged(e.target.value)}
                        value={currentExamSection.slug}
                      >
                        {
                          props.examSectionDetails.map((examSection) => (
                            <option
                              key={examSection.slug}
                              value={examSection.slug}
                            >
                              {examSection.name}
                            </option>
                          ))
                        }
                      </Select>
                    </div>
                  </>
                )
              }
              
              <hr className="u-margin-B-n u-margin-T-s" />
            </div>

            {
              currentExamSection.subjects && (
                <CardLayout.Card>
                  <PerformanceBreakdown subjects={currentExamSection.subjects} />
                </CardLayout.Card>
              )
            }

            {
              currentExamSection.answersBySection ? (
                <div className="d-flex flex-column gap-m">
                  {
                    currentExamSection.answersBySection.map((section, idx) => (
                      <div key={idx} className="d-flex flex-column gap-m">
                        <div className="d-flex flex-column gap-s gap-lg-m">
                          <h3 className="flex-1 u-margin-A-n u-margin-T-s">
                            <strong>{section.name}</strong>
                          </h3>

                          <hr className="u-margin-A-n" />
                        </div>

                        <CardLayout.Card style={{ padding: 0 }}>
                          <AnswersTable
                            answers={section.answers}
                            linkComponent={ContextualLink}
                          />
                        </CardLayout.Card>
                      </div>
                    ))
                  }
                </div>
              ) : (
                <CardLayout.Card>
                  <div className="bg-gray-lightest border-radius-all d-flex gap-m u-border-A-s u-padding-A-m">
                    <MagnifyingGlassIcon fill={colors.grayLight} />

                    <div className="d-flex flex-1 flex-column justify-content-center">
                      <strong>No Data to Display for {currentExamSection.name}</strong>
                      <span>You didn’t answer any questions in this section.</span>
                    </div>
                  </div>
                </CardLayout.Card>
              )
            }
          </div>
        </div>
      </div>
    </>
  );
};

const AITutorNextStep: React.FC<{ branding: any }> = (props) => {
  const launchTutor = () => {
    const launchTutorEvent = new CustomEvent(
      'aiTutor.launchSkill',
      {
        detail: {
          domain: {
            klass: 'TestPrep::AI::TutoringSessions::Domains::Tutor',
            name: `${props.branding.examName} Tutor`
          }
        }
      }
    );
    document.dispatchEvent(launchTutorEvent);
  };

  return (
    <CardLayout.Card className="d-flex flex-1" style={{ gap: 30, height: '100%' }}>
      <div
        className="align-items-center d-flex flex-none justify-content-center"
        style={{
          background: 'linear-gradient(to right, #26B67C, #9B51E0)',
          borderRadius: '50%',
          boxShadow: 'rgba(0, 0, 0, 0.15) 0px 4px 24px 0px, inset rgba(0, 0, 0, 0.15) 0px 0px 0px 3px',
          color: '#fff',
          height: 60,
          paddingTop: 3,
          width: 60
        }}
      >
        <GraduateCapIcon size={30} />
      </div>

      <div className="d-flex flex-column">
        <div className="d-flex">
          <GradientPill size="sm">
            {props.branding.examName} AI Tutor
          </GradientPill>
        </div>

        <div className="text-gray-darker u-margin-V-xxs">
          <strong>Analyze Your Performance With Your AI Tutor</strong>
        </div>

        <div className="text-gray-light">
          <ul className="list-unstyled">
            <li>"How did I do on this test?"</li>
            <li>"Is this a good score?"</li>
            <li>"What can I do to improve my score?"</li>
          </ul>
        </div>

        <div>
          <a
            className="small"
            onClick={launchTutor}
            style={{ color: colors.purple }}
          >
            Launch the AI Tutor
          </a>
        </div>
      </div>
    </CardLayout.Card>
  );
};

const DecideOnNextTestNextStep: React.FC<{}> = () => {
  return (
    <CardLayout.Card className="d-flex flex-1" style={{ gap: 30, height: '100%' }}>
      <div>
        <WateringSeedlingIcon fill={colors.brandPrimary} height={80} width={80} />
      </div>

      <div>
        <div>
          <small className="text-gray-light"><strong>NEXT STEPS</strong></small>
        </div>

        <div className="text-gray-darker u-margin-V-xxs">
          <strong>Decide When You'll Take Your Next Test</strong>
        </div>

        <div className="text-gray-light">
          Whether you are close to your goal, or still have a ways to go, we recommend taking a practice test about every 2 weeks. Set aside the time on your calendar now and commit to it!
        </div>
      </div>
    </CardLayout.Card>
  );
};

const ReviewPerformanceNextStep: React.FC<{ mockTest: any }> = (props) => {
  const { features } = useAppContext().data;

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  return (
    <CardLayout.Card className="d-flex flex-1" style={{ gap: 30, height: '100%' }}>
      <div>
        <LightBulbPlantIcon fill={colors.purple} height={80} width={80} />
      </div>

      <div>
        <div>
          <small className="text-gray-light"><strong>NEXT STEPS</strong></small>
        </div>

        <div className="text-gray-darker u-margin-V-xxs">
          <strong>Review Your Performance</strong>
        </div>

        <div className="text-gray-light">
          Review your performance breakdown below to find areas where you can improve. For questions you got wrong watch our expert video explanations to see how to approach the question next time.
        </div>

        {
          features.analytics && (
            <div className="align-items-center d-flex gap-s u-margin-T-s">
              <div className="small">
                <ContextualLink
                  href={paths.analytics({ focus: `Practice Test - ${moment(props.mockTest.finishedAt * 1000).tz(timeZone).format('MMM D')}:${props.mockTest.id}` })}
                  style={{ color: colors.purple }}
                >
                  Take a deep dive into your analytics
                </ContextualLink>
              </div>
            </div>
          )
        }
      </div>
    </CardLayout.Card>
  );
};

export default MockTestResults;
